import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core'
import { VirtualKeyboardService } from './virtual-keyboard.service'
import { VirtualKeyboardLayout } from './virtual-keyboard.types'

/**
 * Espera entre o campo perder o foco e o teclado fechar — tempo de um toque se
 * completar (mousedown -> mouseup) sem o layout mudar debaixo do dedo.
 */
const CLOSE_DELAY_MS = 250

/**
 * Liga um `<input>` ao teclado virtual do totem.
 *
 * Uso: `<input matInput appVirtualKeyboard="numeric" vkLabel="Celular">`
 *
 * Além de abrir o teclado ao focar/tocar no campo, a diretiva aplica
 * `inputmode="none"`, que impede o Windows de abrir o teclado virtual dele nos
 * equipamentos onde ele ainda funciona — evitando dois teclados na tela.
 */
@Directive({
  selector: 'input[appVirtualKeyboard]',
})
export class VirtualKeyboardDirective implements OnInit, OnDestroy {
  /** Layout de teclas do campo. Default: alfanumérico. */
  @Input('appVirtualKeyboard') layout: VirtualKeyboardLayout | '' = 'alphanumeric'
  /** Rótulo exibido no cabeçalho do teclado. Sem valor, cai no placeholder/aria-label. */
  @Input() vkLabel: string = ''

  private closeTimer?: ReturnType<typeof setTimeout>

  constructor(
    private elementRef: ElementRef<HTMLInputElement>,
    private keyboardService: VirtualKeyboardService
  ) {}

  private cancelCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      this.closeTimer = undefined
    }
  }

  ngOnInit(): void {
    // RC8 — sem teclado nativo do Windows por cima do nosso.
    this.elementRef.nativeElement.setAttribute('inputmode', 'none')
    this.elementRef.nativeElement.setAttribute('autocomplete', 'off')
  }

  ngOnDestroy(): void {
    this.cancelCloseTimer()
    this.keyboardService.closeIfTarget(this.elementRef.nativeElement)
  }

  @HostListener('focus')
  @HostListener('click')
  onActivate(): void {
    this.cancelCloseTimer()
    const input = this.elementRef.nativeElement
    const maxLengthAttr = input.getAttribute('maxlength')

    this.keyboardService.open({
      input,
      layout: this.layout || 'alphanumeric',
      label: this.resolveLabel(),
      maxLength: maxLengthAttr ? Number(maxLengthAttr) : undefined,
    })
  }

  /**
   * Fecha o teclado quando o campo perde o foco de verdade. Tocar nas teclas nao
   * cai aqui (o painel cancela o mousedown, o campo nunca perde o foco), entao
   * isto so dispara quando o foco vai para outro lugar — por exemplo ao abrir o
   * calendario da Validade, que cobre o teclado com o backdrop dele e o deixaria
   * visivel porem morto.
   */
  @HostListener('blur')
  onDeactivate(): void {
    // O fechamento espera o toque terminar. O blur chega no mousedown; fechar ali
    // recolhe o teclado e o conteudo desce ANTES do mouseup, entao o botao sai de
    // baixo do dedo e o toque se perde. Se o foco tiver ido para outro campo com
    // teclado, o closeIfTarget ja nao fecha (o campo ativo passou a ser o outro).
    this.closeTimer = setTimeout(() => {
      this.keyboardService.closeIfTarget(this.elementRef.nativeElement)
    }, CLOSE_DELAY_MS)
  }

  /** Rótulo explícito > placeholder > aria-label > vazio. */
  private resolveLabel(): string {
    const input = this.elementRef.nativeElement
    return this.vkLabel || input.placeholder || input.getAttribute('aria-label') || ''
  }
}
